import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Card, Table, Button, Alert, Badge, Spinner, Form } from 'react-bootstrap';
import {
  useGetCrmMigrationQuery,
  useRunCrmMigrationStepMutation,
  useLazyGetCrmMigrationLogQuery,
} from '../app/api';

// Super Admin -> CRM Migration.
//
// The Salentica->FSC document pipeline as one page: three stages that feed each
// other, each runnable and watchable, with the counts they produce kept where
// the next stage can be judged against them.
//
// The page exists because the sequence has a silent step. The extractor writes
// S3 and the manifest; the FSC loader reads the `documents` catalog; the import
// between them appears in neither script's output. Run extract then link and
// you get "0 to link" — which reads as "done" and means "none of it arrived".
// So the gap between the manifest and the catalog is the number this page leads
// with, and stage 2 says out loud what it is for.

const STATUS = {
  queued:  { bg: 'secondary', label: 'Queued' },
  running: { bg: 'info',      label: 'Running' },
  success: { bg: 'success',   label: 'Success' },
  error:   { bg: 'danger',    label: 'Failed' },
};

function when(ts) {
  if (!ts) return '—';
  const d = new Date(ts.endsWith?.('Z') ? ts : `${ts}Z`);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

const n = (v) => (v == null ? '—' : Number(v).toLocaleString());

// One line summarising a finished run, per stage. The history table and the
// "last run" line under each stage both use this, so they cannot describe the
// same run differently.
function describe(step, run) {
  const d = run?.detail;
  if (typeof d !== 'object' || d === null) {
    return <span className={run?.status === 'error' ? 'text-danger' : 'text-muted'}>
      {d || run?.progress || '—'}
    </span>;
  }
  if (step === 'extract') {
    return <>
      {n(d.uploaded)} uploaded, {n(d.deduped)} deduplicated, {n(d.skipped)} already done
      {d.errored > 0 && <span className="text-danger"> · {n(d.errored)} errored</span>}
    </>;
  }
  if (step === 'sync_manifest') {
    return <>
      <strong>{n(d.inserted)} imported</strong> · {n(d.already_present)} already catalogued
      {' '}({n(d.client_docs)} client, {n(d.rep_docs)} rep)
    </>;
  }
  // link
  return <>
    {d.created != null
      ? <strong>{n(d.created)} created in FSC</strong>
      : <>{n(d.to_link)} to link</>}
    {' '}· {n(d.already_linked)} already linked · {n(d.duplicate_bytes)} duplicate bytes
    {' '}· {n(d.unresolvable)} unresolvable
    {d.errors > 0 && <span className="text-danger"> · {n(d.errors)} insert error(s)</span>}
  </>;
}

// Captured stdout. Fetched on demand and re-fetched while its run is in flight,
// so "watch it work" and "read what it did afterwards" are the same control.
function RunLog({ runId, live }) {
  const [fetchLog, { data, isFetching }] = useLazyGetCrmMigrationLogQuery();

  useEffect(() => {
    if (!runId) return undefined;
    fetchLog(runId);
    if (!live) return undefined;
    const t = setInterval(() => fetchLog(runId), 4000);
    return () => clearInterval(t);
  }, [runId, live, fetchLog]);

  if (!runId) return null;
  return (
    <pre
      className="bg-dark text-light rounded p-3 mb-0 mt-2"
      style={{ fontSize: '0.72rem', maxHeight: '26rem', overflow: 'auto', whiteSpace: 'pre-wrap' }}
    >
      {data?.log || (isFetching ? 'Loading…' : 'No output recorded for this run.')}
    </pre>
  );
}
RunLog.propTypes = {
  runId: PropTypes.number,
  // True only for the run currently in flight — the transcript re-fetches on a
  // timer for that one and is read once for every other.
  live: PropTypes.bool,
};

function StepCard({ step, last, active, busy, onRun, warnStale }) {
  const [live, setLive] = useState(false);
  const running = active && active.step === step.key;
  const liveOnly = step.live_only;
  const writes = liveOnly || live;

  return (
    <Card className={`mb-3 ${running ? 'border-info' : ''}`}>
      <Card.Body>
        <div className="d-flex align-items-start gap-3">
          <Badge bg={running ? 'info' : 'secondary'} className="mt-1"
                 style={{ fontSize: '0.85rem', minWidth: '1.9rem' }}>
            {step.order}
          </Badge>
          <div className="flex-grow-1">
            <h5 className="mb-1">{step.label}</h5>
            <p className="text-muted mb-2" style={{ fontSize: '0.85rem', maxWidth: '46rem' }}>
              {step.summary}
            </p>
            <div className="text-muted mb-3" style={{ fontSize: '0.76rem' }}>
              <i className="bi bi-pencil-square" /> Writes to <code>{step.writes}</code>
            </div>

            {warnStale && (
              <Alert variant="warning" className="py-2" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-exclamation-triangle-fill" />{' '}
                <strong>The last dry run predates the last import.</strong> Its numbers
                describe the catalog as it was before those documents arrived. Run the
                dry run again before writing to Salesforce.
              </Alert>
            )}

            {running ? (
              <Alert variant="info" className="d-flex align-items-center gap-2 mb-0">
                <Spinner animation="border" size="sm" />
                <div>
                  <strong>{active.live ? 'Running' : 'Dry run'}</strong>
                  {' — '}{active.progress || 'starting…'}
                  <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                    Started by {active.started_by} at {when(active.started)}
                  </div>
                </div>
              </Alert>
            ) : (
              <>
                {!liveOnly && (
                  <Form.Check
                    type="switch"
                    id={`live-${step.key}`}
                    className="mb-2"
                    checked={live}
                    onChange={(e) => setLive(e.target.checked)}
                    label={
                      <>
                        <strong>Write changes</strong>
                        <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                          {' '}— off means a dry run: {step.dry_run_note}.
                        </span>
                      </>
                    }
                  />
                )}
                <Button
                  variant={writes ? 'primary' : 'outline-primary'}
                  onClick={() => onRun(step.key, live)}
                  disabled={busy}
                >
                  {busy ? <Spinner animation="border" size="sm" /> : <i className="bi bi-play-fill" />}
                  {' '}{liveOnly ? `Run step ${step.order}` : (live ? `Run step ${step.order} for real` : 'Run a dry run')}
                </Button>
                {liveOnly && (
                  <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>
                    No dry run — this step either imports or does nothing.
                  </span>
                )}
                <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
                  {step.duration_note}
                </div>
              </>
            )}

            {last && !running && (
              <div className="mt-3 pt-2 border-top" style={{ fontSize: '0.8rem' }}>
                <Badge bg={(STATUS[last.status] || {}).bg || 'secondary'} className="me-2">
                  {(STATUS[last.status] || {}).label || last.status}
                </Badge>
                <span className="text-muted">
                  Last run {when(last.finished)} by {last.started_by}
                  {' '}({last.live ? 'live' : 'dry run'})
                </span>
                <div className="mt-1">{describe(step.key, last)}</div>
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
StepCard.propTypes = {
  // One entry of integrations/crm_migration.STEPS, passed through from the API
  // so the copy describing a stage lives next to the code that runs it.
  step: PropTypes.shape({
    key: PropTypes.string.isRequired,
    order: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    summary: PropTypes.string,
    writes: PropTypes.string,
    live_only: PropTypes.bool,
    dry_run_note: PropTypes.string,
    duration_note: PropTypes.string,
  }).isRequired,
  last: PropTypes.object,
  active: PropTypes.object,
  busy: PropTypes.bool,
  onRun: PropTypes.func.isRequired,
  warnStale: PropTypes.bool,
};

export default function CrmMigration() {
  const { data, isLoading, refetch } = useGetCrmMigrationQuery();
  const [run, { isLoading: isTriggering }] = useRunCrmMigrationStepMutation();
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [openLog, setOpenLog] = useState(null);

  const steps = data?.steps || [];
  const state = data?.state || {};
  const active = data?.active;
  const lastByStep = data?.last_by_step || {};
  const runs = data?.runs || [];

  // Poll only while something is in flight. An extraction runs for hours, and a
  // page left open overnight should not spend it hitting the API.
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(refetch, 4000);
    return () => clearInterval(t);
  }, [active, refetch]);

  // Follow the active run's transcript automatically — the point of the page is
  // watching a stage work, and that shouldn't need a second click.
  useEffect(() => {
    if (active?.id) setOpenLog(active.id);
  }, [active?.id]);

  const go = useCallback(async (step, live) => {
    setError('');
    setNote('');
    try {
      const res = await run({ step, live }).unwrap();
      setNote(res.live
        ? 'Started — this run writes.'
        : 'Dry run started — nothing will be written.');
      setOpenLog(res.run_id);
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not start the run.');
    }
  }, [run, refetch]);

  if (isLoading) {
    return <Container className="py-4 text-center"><Spinner animation="border" /></Container>;
  }

  const pending = state.pending_import ?? 0;

  return (
    <Container className="py-4">
      <h3 className="mb-1">CRM Migration</h3>
      <p className="text-muted" style={{ maxWidth: '48rem' }}>
        The Salentica → FSC document pipeline. Files are extracted from the
        Salentica org into S3, imported into the Hub’s document catalog, and then
        linked to households in Salesforce FSC. Each stage feeds the next, so run
        them in order — and note that a link run can only ever see what stage 2
        has imported.
      </p>

      {state.available === false ? (
        <Alert variant="secondary">
          <strong>No extraction manifest in this environment.</strong> The Salentica
          corpus is built in dev only, so these stages have nothing to read here.
          Catalog rows are copied between environments rather than re-extracted —
          see <code>docs/dev-prod-crm-data-parity.md</code>.
        </Alert>
      ) : (
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex gap-4 flex-wrap" style={{ fontSize: '0.88rem' }}>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Extracted to S3</div>
                <strong style={{ fontSize: '1.1rem' }}>{n(state.uploaded)}</strong>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>In the document catalog</div>
                <strong style={{ fontSize: '1.1rem' }}>{n(state.catalogued)}</strong>
              </div>
              {/* The hero figure: the one number that tells you whether the
                  pipeline is actually finished. Status colour is never the only
                  channel — the icon and the label carry it too. */}
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Extracted but not yet catalogued
                </div>
                <strong
                  className={pending > 0 ? 'text-warning' : 'text-success'}
                  style={{ fontSize: '1.6rem', lineHeight: 1.1 }}
                >
                  {pending > 0 && <i className="bi bi-exclamation-triangle-fill me-2"
                                     style={{ fontSize: '1rem' }} />}
                  {n(pending)}
                </strong>
              </div>
              {state.errored_manifest > 0 && (
                <div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>Extraction errors</div>
                  <strong className="text-danger" style={{ fontSize: '1.1rem' }}>
                    <i className="bi bi-x-octagon-fill me-1" style={{ fontSize: '0.85rem' }} />
                    {n(state.errored_manifest)}
                  </strong>
                </div>
              )}
            </div>

            {pending > 0 && (
              <Alert variant="warning" className="py-2 mb-0 mt-3" style={{ fontSize: '0.84rem' }}>
                <i className="bi bi-exclamation-triangle-fill" />{' '}
                <strong>{n(pending)} extracted file(s) are not in the catalog yet.</strong>{' '}
                Until step 2 runs they are invisible to the FSC loader, to document
                search, and to the client portal — and a link run will report
                nothing to do.
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {note && <Alert variant="success" dismissible onClose={() => setNote('')}>{note}</Alert>}

      {steps.map(s => (
        <StepCard
          key={s.key}
          step={s}
          last={lastByStep[s.key]}
          active={active}
          busy={isTriggering || !!active}
          onRun={go}
          warnStale={s.key === 'link' && data?.dry_run_stale}
        />
      ))}

      <h5 className="mb-2 mt-4">Recent runs</h5>
      <Card className="mb-3">
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th style={{ width: '10%' }}>Status</th>
              <th style={{ width: '18%' }}>Step</th>
              <th style={{ width: '9%' }}>Mode</th>
              <th style={{ width: '16%' }}>Started</th>
              <th>Result</th>
              <th style={{ width: '6%' }} />
            </tr>
          </thead>
          <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
            {runs.map(r => {
              const s = STATUS[r.status] || { bg: 'secondary', label: r.status };
              const meta = steps.find(x => x.key === r.step);
              return (
                <React.Fragment key={r.id}>
                  <tr>
                    <td><Badge bg={s.bg}>{s.label}</Badge></td>
                    <td style={{ fontSize: '0.82rem' }}>{meta?.label || r.step}</td>
                    {/* `live` arrives as MySQL's 0/1, which the ternary handles
                        directly — no Boolean() needed when it isn't a && guard. */}
                    <td style={{ fontSize: '0.82rem' }}>{r.live ? 'Live' : 'Dry run'}</td>
                    <td style={{ fontSize: '0.82rem' }}>
                      {when(r.started)}
                      <div className="text-muted" style={{ fontSize: '0.72rem' }}>{r.started_by}</div>
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>{describe(r.step, r)}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0"
                        onClick={() => setOpenLog(openLog === r.id ? null : r.id)}
                      >
                        {openLog === r.id ? 'Hide' : 'Log'}
                      </Button>
                    </td>
                  </tr>
                  {openLog === r.id && (
                    <tr>
                      <td colSpan={6} className="bg-body-secondary">
                        <RunLog runId={r.id} live={active?.id === r.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {runs.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-4">
                Nothing has been run from this page yet.
              </td></tr>
            )}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
}
